import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";

import { EmptyMessage, ErrorView, PageTitle } from "@components/shared";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

const PAGE_SIZE = 50;

export default function PermissionDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminsPage, setAdminsPage] = useState(0);
  const [orgUsersPage, setOrgUsersPage] = useState(0);

  useEffect(() => {
    fetchData();
  }, [id, adminsPage, orgUsersPage]);

  function fetchData() {
    setLoading(true);
    axios
      .post(`/retrieve-permission/`, {
        permission_id: id,
        admins_page: adminsPage,
        admins_page_size: PAGE_SIZE,
        organization_users_page: orgUsersPage,
        organization_users_page_size: PAGE_SIZE,
      })
      .then(({ data }) => {
        setPayload(data.response[0] || null);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setPayload(null);
        setLoading(false);
        toast.error(err);
      });
  }

  if (error && !payload) {
    return <ErrorView text={error} />;
  }

  if (loading && !payload) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!payload) {
    return null;
  }

  const permission = payload.permission || {};
  const stats = payload.stats || {};
  const roles = payload.roles || [];
  const admins = payload.admins || { data: [], total: 0, page_count: 0 };
  const organizationUsers = payload.organization_users || {
    data: [],
    total: 0,
    page_count: 0,
  };

  return (
    <Stack gap="md">
      <PageTitle
        title={permission.name || t("permission_singular")}
        subtitle={t("permission_singular")}
        icon={entityIcon.role()}
        iconColor={entityColor.role}
      />

      <Card>
        <Stack gap="xs">
          <Group gap="xs">
            {permission.key && (
              <Badge variant="light">{permission.key}</Badge>
            )}
            {permission.group && (
              <Badge variant="outline" color="gray">
                {permission.group}
              </Badge>
            )}
            {permission.code != null && (
              <Text size="xs" c="dimmed">
                code {permission.code}
              </Text>
            )}
          </Group>
          {permission.description && (
            <Text size="sm" c="dimmed">
              {permission.description}
            </Text>
          )}
          <Group gap="xs" mt="xs">
            <Badge variant="light" color="gray">
              {stats.role_count || 0} {t("role_plural")}
            </Badge>
            <Badge variant="light" color="gray">
              {stats.admin_count || 0} {t("admin_plural")}
            </Badge>
            <Badge variant="light" color="gray">
              {stats.organization_user_count || 0}{" "}
              {t("permission_org_user_holders")}
            </Badge>
          </Group>
        </Stack>
      </Card>

      <Stack gap="xs">
        <Title order={5}>{t("role_plural")}</Title>
        {!roles.length && <EmptyMessage />}
        {roles.map((role) => (
          <Card key={role.id} component={Link} to={`/roles/${role.id}`}>
            <Group justify="space-between">
              <Text fw={700} c="dark">
                {role.name}
              </Text>
              {role.default && (
                <Badge size="sm" variant="light" color="blue">
                  {t("default")}
                </Badge>
              )}
            </Group>
          </Card>
        ))}
      </Stack>

      <Stack gap="xs">
        <Title order={5}>
          {t("admin_plural")} ({admins.total || 0})
        </Title>
        {!admins.data?.length && <EmptyMessage />}
        {admins.data?.map((admin) => (
          <Card key={admin.id} component={Link} to={`/admins/${admin.id}`}>
            <Text fw={700} c="dark">
              {admin.first_name} {admin.last_name}
            </Text>
            <Text size="sm" c="dimmed">
              {admin.email}
            </Text>
          </Card>
        ))}
        <PaginationRow
          page={adminsPage}
          pageCount={admins.page_count || 0}
          onChange={setAdminsPage}
          disabled={loading}
        />
      </Stack>

      <Stack gap="xs">
        <Title order={5}>
          {t("permission_org_user_holders")} (
          {organizationUsers.total || 0})
        </Title>
        {!organizationUsers.data?.length && <EmptyMessage />}
        {organizationUsers.data?.map((row) => (
          <Card
            key={`${row.organization_id}-${row.user_id}`}
            component={Link}
            to={`/users/${row.user_id}`}
          >
            <Text fw={700} c="dark">
              {row.first_name} {row.last_name}
            </Text>
            <Text size="sm" c="dimmed">
              {row.email}
            </Text>
            <Text size="sm" c="dimmed">
              {row.organization_name || row.organization_id}
            </Text>
          </Card>
        ))}
        <PaginationRow
          page={orgUsersPage}
          pageCount={organizationUsers.page_count || 0}
          onChange={setOrgUsersPage}
          disabled={loading}
        />
      </Stack>
    </Stack>
  );
}

function PaginationRow({ page, pageCount, onChange, disabled }) {
  const { t } = useTranslation();
  if (!pageCount || pageCount <= 1) {
    return null;
  }

  return (
    <Flex gap="sm" justify="flex-end" align="center">
      <Button
        size="xs"
        variant="light"
        disabled={disabled || page <= 0}
        onClick={() => onChange(page - 1)}
      >
        {t("previous")}
      </Button>
      <Text size="sm" c="dimmed">
        {page + 1} / {pageCount}
      </Text>
      <Button
        size="xs"
        variant="light"
        disabled={disabled || page + 1 >= pageCount}
        onClick={() => onChange(page + 1)}
      >
        {t("next")}
      </Button>
    </Flex>
  );
}
